// @flow
import React, { useMemo } from 'react';
import useIntl from '../../hooks/useIntl';
import {
  AccountLabel,
  DrillDownMoney,
  Money,
  NoteCell,
  Variance,
  VariancePercent,
} from './cells';
import messages from './messages';
import type { DrillDown, Note, ReportRow } from './types';

export const useHeaders = (): Array<{ id: string, label: string }> => {
  const intl = useIntl();
  return useMemo(
    () =>
      [
        ['account', messages.account],
        ['monthActual', messages.monthActual],
        ['monthBudget', messages.monthBudget],
        ['monthVariance', messages.monthVariance],
        ['monthVariancePercent', messages.monthVariancePercent],
        ['ytdActual', messages.ytdActual],
        ['ytdBudget', messages.ytdBudget],
        ['ytdVariance', messages.ytdVariance],
        ['ytdVariancePercent', messages.ytdVariancePercent],
        ['notes', messages.notes],
      ].map(([id, message]) => ({ id, label: intl.formatMessage(message) })),
    [intl],
  );
};

const EMPTY_CELL = { value: '' };

export const useRows = ({
  rows,
  notes,
  canEditNotes,
  onSaveNote,
  onDrillDown,
  scopeKey,
  savingAccountNos,
}: {
  rows: Array<ReportRow>,
  notes: { [accountNo: string]: Note },
  canEditNotes: boolean,
  onSaveNote: (accountNo: string, note: string) => void,
  onDrillDown: (drillDown: DrillDown) => void,
  scopeKey: string,
  savingAccountNos: Array<string>,
}): Array<Object> =>
  useMemo(
    () =>
      rows.map((row) => {
        const strong = row.kind !== 'account';
        const account = {
          value: (
            <AccountLabel label={row.label} depth={row.depth} strong={strong} />
          ),
        };
        const { month, ytd, accountNo } = row;
        if (!month || !ytd) {
          return { id: row.key, account };
        }

        const actual = (value: number, range: 'month' | 'ytd') =>
          accountNo ? (
            <DrillDownMoney
              value={value}
              onClick={() =>
                onDrillDown({ accountNo, label: row.label, range })
              }
            />
          ) : (
            <Money value={value} strong={strong} />
          );

        return {
          id: row.key,
          account,
          monthActual: { value: actual(month.actual, 'month') },
          monthBudget: {
            value: <Money value={month.budget} strong={strong} />,
          },
          monthVariance: {
            value: <Variance amounts={month} strong={strong} />,
          },
          monthVariancePercent: {
            value: <VariancePercent amounts={month} strong={strong} />,
          },
          ytdActual: { value: actual(ytd.actual, 'ytd') },
          ytdBudget: { value: <Money value={ytd.budget} strong={strong} /> },
          ytdVariance: { value: <Variance amounts={ytd} strong={strong} /> },
          ytdVariancePercent: {
            value: <VariancePercent amounts={ytd} strong={strong} />,
          },
          notes: accountNo
            ? {
                value: (
                  <NoteCell
                    key={`${scopeKey}:${accountNo}`}
                    note={notes[accountNo]}
                    readOnly={!canEditNotes}
                    saving={savingAccountNos.includes(accountNo)}
                    onSave={(note) => onSaveNote(accountNo, note)}
                  />
                ),
              }
            : EMPTY_CELL,
        };
      }),
    [
      rows,
      notes,
      canEditNotes,
      onSaveNote,
      onDrillDown,
      scopeKey,
      savingAccountNos,
    ],
  );
